"use client"
import { useState, useEffect } from "react"
import { getDatabase, ref, onValue, update } from "firebase/database"
import { FaCheck, FaTimes, FaEye, FaExclamationTriangle } from "react-icons/fa"
import "./paymentConfirmation.css"

const PaymentConfirmation = () => {
  const [pendingPayments, setPendingPayments] = useState([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState({})

  const db = getDatabase()

  useEffect(() => {
    const ordersRef = ref(db, "orders")
    const unsubscribe = onValue(ordersRef, (snapshot) => {
      const orders = []
      snapshot.forEach((userSnap) => {
        userSnap.forEach((orderSnap) => {
          const order = orderSnap.val()
          if (order.payment_status === "pending") {
            orders.push({
              orderId: orderSnap.key,
              userId: userSnap.key,
              ...order,
            })
          }
        })
      })
      setPendingPayments(orders)
      setLoading(false)
    })

    return () => unsubscribe()
  }, [db])

  const handleConfirm = async (orderId, userId) => {
    setActionLoading((prev) => ({ ...prev, [`${userId}-${orderId}`]: true }))
    try {
      const orderRef = ref(db, `orders/${userId}/${orderId}`)
      await update(orderRef, {
        payment_status: "confirmed",
        confirmed_at: new Date().toISOString(),
      })

      // Send notification to user
      await sendNotification(userId, orderId, "Payment Confirmed", `Your payment for order ${orderId} has been confirmed. Dispatch will begin soon.`)
    } catch (err) {
      alert("Failed to confirm payment.")
    } finally {
      setActionLoading((prev) => ({ ...prev, [`${userId}-${orderId}`]: false }))
    }
  }

  const handleReject = async (orderId, userId) => {
    const reason = prompt("Reason for rejection (optional):")
    setActionLoading((prev) => ({ ...prev, [`${userId}-${orderId}`]: true }))
    try {
      const orderRef = ref(db, `orders/${userId}/${orderId}`)
      await update(orderRef, {
        payment_status: "rejected",
        rejection_reason: reason || "Not specified",
        rejected_at: new Date().toISOString(),
      })

      await sendNotification(userId, orderId, "Payment Rejected", `Your payment proof was rejected. Reason: ${reason || "Not specified"}. Please re-upload correct proof.`)
    } catch (err) {
      alert("Failed to reject payment.")
    } finally {
      setActionLoading((prev) => ({ ...prev, [`${userId}-${orderId}`]: false }))
    }
  }

  const sendNotification = async (userId, orderId, title, message) => {
    const notifRef = ref(db, `user_notifications/Payment Status/${userId}/${Date.now()}`)
    await update(notifRef, {
      title,
      message,
      orderId,
      timestamp: new Date().toISOString(),
      seen: false,
    })
  }

  if (loading) return <div className="loading">Loading pending payments...</div>

  return (
    <div className="payment-confirmation-container">
      <h1>
        Payment Confirmation
        <FaExclamationTriangle style={{ marginLeft: 8, color: "#f39c12" }} />
      </h1>
      {pendingPayments.length === 0 ? (
        <p className="no-data">No pending payments.</p>
      ) : (
        <div className="table-wrapper">
          <table className="payment-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Company</th>
                <th>Amount</th>
                <th>Proof</th>
                <th>UTR</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pendingPayments.map((order) => {
                const key = `${order.userId}-${order.orderId}`
                const isLoading = actionLoading[key]
                return (
                  <tr key={key}>
                    <td>{order.orderId}</td>
                    <td>{order.companyName}</td>
                    <td>₹{order.paymentAmount?.toLocaleString()}</td>
                    <td>
                      {order.paymentFileUrl ? (
                        <a href={order.paymentFileUrl} target="_blank" rel="noopener noreferrer">
                          <FaEye /> View
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{order.utrNumber || "—"}</td>
                    <td className="actions">
                      <button
                        className="btn-confirm"
                        onClick={() => handleConfirm(order.orderId, order.userId)}
                        disabled={isLoading}
                      >
                        <FaCheck /> {isLoading ? "..." : "Confirm"}
                      </button>
                      <button
                        className="btn-reject"
                        onClick={() => handleReject(order.orderId, order.userId)}
                        disabled={isLoading}
                      >
                        <FaTimes /> {isLoading ? "..." : "Reject"}
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default PaymentConfirmation